import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useAuth} from '@/contexts/AuthContext';
import {ReitService as reitService} from '@/services/investments';

/**
 * A custom React hook that provides a mutation function to delete a REIT dividend payment.
 * The payment's cash transaction or reinvested units are reversed with it, so the payments,
 * holdings, accounts and transactions queries are invalidated on success.
 *
 * @return {object} Returns the mutation object provided by the `useMutation` hook.
 * The mutation object includes properties and methods such as `mutate`, `mutateAsync`,
 * `isLoading`, `isSuccess`, etc., to handle the mutation lifecycle.
 */
export default function useDeleteReitDividendPayment() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (id: string) => {
      return await reitService.deleteDividendPayment(user!.id, id);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reit_dividend_payments'] });
      qc.invalidateQueries({ queryKey: ['reit_holdings'] });
      qc.invalidateQueries({ queryKey: ['accounts'] });
      qc.invalidateQueries({ queryKey: ['transactions'] });
    },
  });
}
